import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { haptics } from '@/shared/lib/telegram';
import { cn } from '@/shared/lib/cn';

const ROW_HEIGHT_PX = 48;
/** Строк видно одновременно: 2 выше + выбранная + 2 ниже — как в пикере времени iOS. */
const VISIBLE_ROWS = 5;
export const REEL_VIEWPORT_HEIGHT_PX = ROW_HEIGHT_PX * VISIBLE_ROWS;
const CENTER_OFFSET_PX = (REEL_VIEWPORT_HEIGHT_PX - ROW_HEIGHT_PX) / 2;

// Список рендерится несколько раз подряд, а не по кругу через css-scroll-loop:
// спин просто едет по этой длинной ленте на N строк вперёд — без стороннего
// движка карусели и его собственной физики скролла, которая не даёт
// предсказуемо быстро и синхронно попадать в нужный снап при частых
// программных прыжках. После остановки координата перебазируется в среднюю
// копию, чтобы следующий спин не укатился за пределы отрендеренного.
const CENTER_COPY = 2;

// Насколько гаснут строки по мере удаления от центра — соседние строки
// притушены заметнее выбранной, но не пропадают совсем.
const OPACITY_FALLOFF = 0.5;
const MIN_OPACITY = 0.1;
const PAINT_RADIUS = 3;

// Меньше скольких строк барабан за спин не проезжает и сколько это идёт по
// времени — общий бюджет фиксирован, а темп внутри него задаёт исключительно
// кривая изинга (см. `easeOutQuint`), а не переменный шаг между тиками. Сколько
// строк сверх минимума — решает выпавшая строка (см. `spin`): до
// `SPIN_ROWS_MIN + count - 1`.
const SPIN_ROWS_MIN = 20;
const SPIN_DURATION_MS = 4200;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function randomIndex(count: number) {
  return Math.floor(Math.random() * count);
}

// Резкий старт и долгий, плавно затухающий хвост: барабан срывается с места и
// заметно докручивается медленнее к самой остановке, а не тормозит одним
// рывком в конце, как было с линейно растущей паузой между шагами раньше.
function easeOutQuint(t: number): number {
  return 1 - (1 - t) ** 5;
}

export interface ReelItem {
  key: string;
  label: string;
}

interface ReelProps {
  items: readonly ReelItem[];
  /** Инкремент — сигнал «начать новый спин»; 0 значит «ещё ни разу не крутили». */
  spinTrigger: number;
  onSelect: (index: number) => void;
  onSpinningChange: (spinning: boolean) => void;
  ariaLabel: (item: ReelItem) => string;
  /**
   * Какую строку выбросить в этом спине. По умолчанию — любую, кроме той, на
   * которой барабан уже стоит после прошлого спина.
   */
  pickIndex?: (count: number) => number;
  className?: string;
}

/**
 * Один барабан пикера — вертикальная лента в духе выбора времени в будильнике
 * iOS: едет сама, один твин на весь спин с ease-out-кривой (см. `animateTo`), и
 * хаптик на каждой строке по пути (в начале — частые тики слитно, к концу —
 * редкие и различимые, как настоящий тормозящий диск).
 *
 * Список рендерится «голым», без своей рамки выбора и подписей — те общие для
 * нескольких барабанов сразу (см. `TopicRouletteSheet`, барабаны темы и подтемы
 * side-by-side, как минуты и секунды).
 */
export function Reel({
  items,
  spinTrigger,
  onSelect,
  onSpinningChange,
  ariaLabel,
  pickIndex,
  className,
}: ReelProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const offsetRef = useRef(0);
  const frameRef = useRef<number | undefined>(undefined);
  const lastRowRef = useRef(0);
  const isSpinningRef = useRef(false);
  // Стоит ли лента на результате прошлого спина или на стартовой строке 0 после
  // смены `items`. Во втором случае исключать текущую строку нельзя: подтема 0
  // иначе не выпадала бы никогда — барабан подтем сбрасывается на неё при
  // каждой новой теме.
  const hasSpunRef = useRef(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Индекс сбрасывается на 0 в тот же рендер, где сменился `items` (сменилась
  // тема — сменился и список подтем) — паттерн «adjusting state during
  // render» из доки React, а не setState внутри эффекта: `prevItems` ловит
  // именно смену идентичности массива, не смену его содержимого на месте.
  const [prevItems, setPrevItems] = useState(items);
  if (items !== prevItems) {
    setPrevItems(items);
    setSelectedIndex(0);
  }

  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const geometry = useCallback(() => {
    const count = itemsRef.current.length || 1;
    const loopPx = count * ROW_HEIGHT_PX;
    return { count, loopPx, centerBasePx: CENTER_COPY * loopPx };
  }, []);

  const writeTransform = useCallback((px: number) => {
    if (trackRef.current)
      trackRef.current.style.transform = `translateY(${CENTER_OFFSET_PX - px}px)`;
  }, []);

  // Диапазон строк, покрашенных прошлым вызовом — нужен, чтобы гасить его
  // край, не попавший в новый диапазон. Раньше без этого при возврате ленты
  // в ту же окрестность (а после сброса на новый `items` она всегда съезжает
  // в одно и то же место, см. `centerBasePx`) оставался кадр непогашенной
  // покраски от позапрошлой позиции поверх текущей — пара соседних строк
  // «пропадала» (не текстом, только непрозрачностью — ровно то, что чинит
  // этот диапазон).
  const paintedRangeRef = useRef<[number, number] | null>(null);

  const paintNear = useCallback((px: number) => {
    const centerRowFractional = px / ROW_HEIGHT_PX;
    const centerRow = Math.round(centerRowFractional);
    const from = centerRow - PAINT_RADIUS;
    const to = centerRow + PAINT_RADIUS;

    const prevRange = paintedRangeRef.current;
    if (prevRange) {
      for (let g = prevRange[0]; g <= prevRange[1]; g++) {
        if (g >= from && g <= to) continue;
        const node = rowRefs.current[g];
        if (node) node.style.opacity = String(MIN_OPACITY);
      }
    }

    for (let g = from; g <= to; g++) {
      const node = rowRefs.current[g];
      if (!node) continue;
      const distance = Math.abs(g - centerRowFractional);
      node.style.opacity = String(clamp(1 - distance * OPACITY_FALLOFF, MIN_OPACITY, 1));
    }
    paintedRangeRef.current = [from, to];
  }, []);

  // Сброс позиции на новый набор строк (сменилась тема — сменился и список
  // подтем) — без анимации, той же логикой, что и первая покраска при
  // маунте: иначе после смены `items` лента ещё секунду стояла бы на строке
  // из уже неактуального списка.
  useEffect(() => {
    const { centerBasePx } = geometry();
    offsetRef.current = centerBasePx;
    hasSpunRef.current = false;
    lastRowRef.current = Math.round(centerBasePx / ROW_HEIGHT_PX);
    writeTransform(centerBasePx);
    paintNear(centerBasePx);
  }, [items, geometry, writeTransform, paintNear]);

  const animateTo = useCallback(
    (toPx: number, durationMs: number, onDone: () => void) => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      const fromPx = offsetRef.current;
      const startTime = performance.now();
      const count = itemsRef.current.length;
      lastRowRef.current = Math.round(fromPx / ROW_HEIGHT_PX);

      // rAF, а не `setTimeout(tick, 16)`: таймер не совпадает с кадрами — на
      // 60 Гц рывки, на 120 Гц iPhone половина частоты.
      const tick = (now: number) => {
        const progress = clamp((now - startTime) / durationMs, 0, 1);
        const px = fromPx + (toPx - fromPx) * easeOutQuint(progress);
        offsetRef.current = px;
        writeTransform(px);
        paintNear(px);

        const row = Math.round(px / ROW_HEIGHT_PX);
        if (row !== lastRowRef.current) {
          lastRowRef.current = row;
          haptics.select();
          // Без `setSelectedIndex`: он нужен только `aria-live`-подписи, а
          // промежуточные строки для скринридера — шум. Состояние на каждую
          // строку перерисовывало весь `repeated`-список (25 рендеров за
          // спин), подсветку и так делает `paintNear` прямо в DOM. Индекс
          // ставится в конце спина.
          onSelect(((row % count) + count) % count);
        }

        if (progress < 1) {
          frameRef.current = requestAnimationFrame(tick);
        } else {
          onDone();
        }
      };

      frameRef.current = requestAnimationFrame(tick);
    },
    [writeTransform, paintNear, onSelect],
  );

  const spin = useCallback(() => {
    if (isSpinningRef.current) return;
    isSpinningRef.current = true;
    onSpinningChange(true);

    // Сперва выбираем строку, потом подгоняем под неё длину проезда. Раньше
    // было наоборот — случайные 20–32 строки от текущей, — и каждое открытие
    // шторки (лента стоит на строке 0) давало одну из 13 тем с 21-й по 33-ю из
    // 50: остальные 37 первым спином не выпадали никогда.
    const { count } = geometry();
    const currentIndex = Math.round(offsetRef.current / ROW_HEIGHT_PX) % count;
    let target: number;
    if (pickIndex) {
      target = pickIndex(count);
    } else if (hasSpunRef.current && count > 1) {
      target = (currentIndex + 1 + randomIndex(count - 1)) % count;
    } else {
      target = randomIndex(count);
    }
    let rowsToTravel = (((target - currentIndex) % count) + count) % count;
    while (rowsToTravel < SPIN_ROWS_MIN) rowsToTravel += count;
    const toPx = offsetRef.current + rowsToTravel * ROW_HEIGHT_PX;

    animateTo(toPx, SPIN_DURATION_MS, () => {
      // Перебазируем координату в среднюю копию ленты: прыжок кратный
      // полному кругу визуально ничего не меняет (те же строки на тех же
      // местах), зато следующий спин не уедет за пределы отрендеренных строк.
      const count = itemsRef.current.length;
      const finalGlobalRow = Math.round(toPx / ROW_HEIGHT_PX);
      const finalIndex = ((finalGlobalRow % count) + count) % count;
      const { centerBasePx } = geometry();
      const rebasedPx = centerBasePx + finalIndex * ROW_HEIGHT_PX;
      offsetRef.current = rebasedPx;
      lastRowRef.current = Math.round(rebasedPx / ROW_HEIGHT_PX);
      writeTransform(rebasedPx);
      paintNear(rebasedPx);

      setSelectedIndex(finalIndex);
      onSelect(finalIndex);

      hasSpunRef.current = true;
      isSpinningRef.current = false;
      onSpinningChange(false);
      haptics.success();
    });
  }, [animateTo, onSpinningChange, onSelect, writeTransform, paintNear, geometry, pickIndex]);

  useEffect(() => {
    if (spinTrigger === 0) return;
    spin();
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      isSpinningRef.current = false;
    };
    // spin пересоздаётся только если поменяются его стабильные зависимости —
    // сигнал к перезапуску здесь именно смена `spinTrigger`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinTrigger]);

  useEffect(
    () => () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  // Копий должно хватать на весь возможный ход спина, а не фиксированные 5:
  // при 50 темах 5 копий и так с запасом, но у 5 подтем это давало всего 25
  // отрендеренных строк — спин на 20+ строк от текущей позиции уезжал за
  // пределы отрендеренного, и лента ехала в пустоту: строк физически не было
  // в DOM на той позиции, куда указывал transform. Отсюда и запас:
  // `CENTER_COPY` + оборот-старт (строка внутри средней копии) + сколько
  // оборотов кладёт самый длинный спин (`SPIN_ROWS_MIN` и до оборота до цели)
  // + оборот под видимые строки ниже центра.
  const repeatCount = useMemo(
    () => CENTER_COPY + 3 + Math.ceil(SPIN_ROWS_MIN / (items.length || 1)),
    [items.length],
  );
  const repeated = Array.from({ length: repeatCount }, () => items).flat();

  return (
    <div className={cn('h-full min-w-0', className)}>
      <div
        className="h-full overflow-hidden"
        style={{
          WebkitMaskImage:
            'linear-gradient(to bottom, transparent 0%, black 30%, black 70%, transparent 100%)',
          maskImage:
            'linear-gradient(to bottom, transparent 0%, black 30%, black 70%, transparent 100%)',
        }}
      >
        <div ref={trackRef} className="flex flex-col will-change-transform">
          {repeated.map((item, globalIndex) => (
            <div
              key={globalIndex}
              ref={(node) => {
                rowRefs.current[globalIndex] = node;
              }}
              className="flex shrink-0 grow-0 items-center justify-center px-1"
              style={{ height: ROW_HEIGHT_PX }}
            >
              {/* Две строки с переносом, а не `truncate`: названия тут длинные
                  («Кафе, рестораны и сервис»), и в одну строку почти каждое
                  упиралось в обе границы колонки — длинные строки распирало во
                  всю ширину, короткие оставались по центру, и колонка выглядела
                  рваной, хотя строки и центрировались. Без `text-balance`: он
                  ровнял длины строк и из «Кафе, рестораны и сервис» делал
                  «Кафе,» + обрезанный хвост, тогда как жадный перенос
                  укладывает то же название в две строки целиком. */}
              <span className="line-clamp-2 min-w-0 text-center text-[15px] leading-tight font-medium text-ink">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div aria-live="polite" className="sr-only">
        {items[selectedIndex] ? ariaLabel(items[selectedIndex]) : null}
      </div>
    </div>
  );
}

export const ROW_HEIGHT = ROW_HEIGHT_PX;

/** Общая рамка выбора над двумя барабанами сразу — они делят одну линию окна. */
export function ReelSelectionWindow() {
  return (
    <div
      aria-hidden="true"
      // Серый `border-hairline`, потолще (2px) для заметности — синий
      // `border-hairline-strong` пробовали, попросили вернуть обратно на серый.
      className="pointer-events-none absolute inset-x-0 top-1/2 z-10 -translate-y-1/2 border-y-2 border-hairline"
      style={{ height: ROW_HEIGHT_PX }}
    />
  );
}
